import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { signIn } from '../services/auth';
import { startCheckout, fetchBillingConfig } from '../services/api';
import { ErrorText, errorMessage } from '../components/ui';
import { LEGAL, TIERS } from '../legalInfo';
import logo from '@assets/ydyai-logo.png';

// onClick={signIn} handed the rejection straight to the window as an unhandled
// promise, so a sign-in that failed before it could redirect was
// indistinguishable from a button that did nothing at all. LoginPage always
// caught this; the public pages, which are where Log in actually gets pressed,
// never did.
function useSignIn() {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function start() {
    setBusy(true);
    setError('');
    try {
      await signIn();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return { start, error, busy };
}

// The sections the header links to, in the order they appear down the page.
// One list drives both the nav and the scroll-spy, so a section can never be
// linked from the header without existing, or exist without being linked.
const SECTIONS = [
  { id: 'about', label: 'About' },
  { id: 'use-cases', label: 'Use cases' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'contact', label: 'Get in touch' },
];

// Hoisted, not derived in the component. As a fresh array on every render it
// would be a new dependency every time, so the observer below would be torn
// down and rebuilt on each scroll-driven re-render - which is every one.
const SECTION_IDS = SECTIONS.map((s) => s.id);

// Which section the reader is currently looking at, for the header's active
// underline.
//
// Deliberately not derived from the URL hash: the hash only changes when a nav
// link is CLICKED, so scrolling by hand would leave the header pointing at
// wherever you last clicked. An IntersectionObserver follows the actual scroll
// position instead.
//
// The current section is the last one whose top has passed under the header.
//
// This began as an IntersectionObserver keyed on intersectionRatio, which was
// wrong in a way worth recording: ratio is the fraction of the SECTION that is
// in view, so a tall section can never score as highly as a short one, and the
// callback only fires when a declared threshold is crossed - between thresholds
// the stored ratio is stale. The underline consequently sat a section behind on
// a phone, where the sections are tallest.
//
// Comparing edges instead is exact at every scroll position and needs no
// thresholds.
//
// The line each section is measured against is its OWN scroll-margin-top -
// the same number the browser parks it at when you click its link. Measuring
// the header instead left the two a pixel apart on a wide screen, where the
// margin is 84px and the header 71px, and a section that had visibly arrived
// still read as not-yet. Taking it from the element means the CSS stays the
// single source of the offset, at every breakpoint, with nothing to keep in
// step by hand.
function useCurrentSection(ids) {
  const [current, setCurrent] = useState('');

  useEffect(() => {
    function pick() {
      let best = '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        // +4 absorbs the sub-pixel rounding a smooth scroll lands on.
        const line = (parseFloat(getComputedStyle(el).scrollMarginTop) || 0) + 4;
        if (el.getBoundingClientRect().top <= line) best = id;
      }

      // The last section is shorter than the run-out below it, so it can never
      // reach the line. Once the page can scroll no further it is what you are
      // looking at, whatever the edges say.
      const atBottom = window.innerHeight + window.scrollY
        >= document.documentElement.scrollHeight - 2;
      if (atBottom) best = ids[ids.length - 1];

      // React bails out when the value is unchanged, so this is a comparison
      // per scroll event rather than a render.
      setCurrent(best);
    }

    pick();
    window.addEventListener('scroll', pick, { passive: true });
    window.addEventListener('resize', pick);
    return () => {
      window.removeEventListener('scroll', pick);
      window.removeEventListener('resize', pick);
    };
  }, [ids]);

  return current;
}

// The public front door: read down the page, then choose a plan or log in.
// Carried over from the old app's pricing page, including the same plan tiers,
// the same checkout contract (POST /api/billing/checkout) and the same legal
// footer.
//
// Written against this app's own CSS tokens rather than the old page's inline
// styles, so it inherits the type scale, spacing and dark mode instead of
// sitting outside them.
//
// `sections` is opt-in because this header is shared with the legal pages and
// the post-checkout page, where those anchors do not exist - a nav pointing at
// #pricing from /privacy would simply do nothing.
export function PublicHeader({ showLogin = true, sections = null, current = '' }) {
  const login = useSignIn();
  return (
    <>
      {/* Three parts, not three items: the nav is centred on the PAGE, and it
          can only be if whatever flanks it is the same width on both sides.
          Left to themselves the logo and the Log in button are not - the nav
          then centres in the gap between them and lands 30px right of centre.
          The two sides are flexible and equal, so the middle is the middle. */}
      <header className="public-header">
        <div className="public-header-side">
          <Link to="/" aria-label="Home">
            <img className="logo" src={logo} alt="your data your ai" />
          </Link>
        </div>
        {sections?.length ? (
          <nav className="public-nav" aria-label="Sections">
            {sections.map((s) => (
              <a
                key={s.id}
                className={`public-nav-link${current === s.id ? ' is-current' : ''}`}
                href={`#${s.id}`}
                aria-current={current === s.id ? 'true' : undefined}
              >
                {s.label}
              </a>
            ))}
          </nav>
        ) : null}
        <div className="public-header-side public-header-side-end">
          {showLogin
            ? (
              <button type="button" className="btn secondary compact" onClick={login.start} disabled={login.busy}>
                {login.busy ? 'Redirecting…' : 'Log in'}
              </button>
            )
            : <Link to="/" className="btn secondary compact">Back to site</Link>}
        </div>
      </header>
      <ErrorText>{login.error}</ErrorText>
    </>
  );
}

export function PublicFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="public-footer">
      <div className="public-footer-inner">
        <div className="public-footer-brand">
          <img className="logo" src={logo} alt="your data your ai" />
          <p className="muted">
            {LEGAL.companyName} · Registered in {LEGAL.registeredIn}<br />
            Company No. {LEGAL.companyNumber}<br />
            {LEGAL.registeredAddress}
          </p>
        </div>
        <div className="public-footer-cols">
          <div>
            <div className="public-footer-heading">Legal</div>
            <div className="public-footer-links">
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
              <Link to="/cookies">Cookie Policy</Link>
            </div>
          </div>
          <div>
            <div className="public-footer-heading">Contact</div>
            <div className="public-footer-links">
              {/* Linked from the footer as well as given to Apple: a Support
                  URL nobody can reach from the site looks like what it would
                  be, a page that exists only to satisfy a form. */}
              <Link to="/support">Support</Link>
              <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>
              <a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a>
            </div>
          </div>
        </div>
      </div>
      <div className="public-footer-legal">© {year} {LEGAL.companyName}. All rights reserved.</div>
    </footer>
  );
}

// The name, read aloud by the page itself: YOUR DATA fades out, YOUR AI fades
// in, and round again.
//
// Both phrases occupy the same grid cell so the line never reflows as they
// swap - a hero that changes height twice every cycle drags the whole page up
// and down with it.
//
// The heading carries the full name as its accessible label and the two
// phrases are hidden from assistive technology, because read in sequence
// "YOUR DATA" / "YOUR AI" is two headings that each say half the name.
function HeroWordmark() {
  return (
    <h1 className="hero-title" aria-label="Your data. Your AI.">
      <span className="hero-swap" aria-hidden="true">
        <span className="hero-phrase hero-phrase-a">Your data</span>
        <span className="hero-phrase hero-phrase-b">Your AI</span>
      </span>
    </h1>
  );
}

const USE_CASES = [
  {
    title: 'Cross-reference a specification against what was supplied',
    body: 'Put the specification and the manufacturer’s data sheet in the same project and ask where they disagree. You get the clause, the corresponding line from the data sheet, and the page each came from.',
  },
  {
    title: 'Find the figure you know is in there somewhere',
    body: 'Rates, quantities, dates, reference numbers. Ask for it in plain English instead of opening a dozen files to remember which one had it.',
  },
  {
    title: 'Make handwritten notes searchable',
    body: 'Scan a page of a site notebook with your phone. The writing is transcribed and indexed like any other document, so a note you made in March is findable in November.',
  },
  {
    title: 'Ask across a whole project, not one file at a time',
    body: 'A question runs against every document in the project at once — contracts, drawings, specifications, spreadsheets, photographs — and the answer says which ones it drew on.',
  },
  {
    title: 'Turn an answer into a document',
    body: 'Any answer can be saved as Word or PDF with its tables intact and its sources listed, ready to send to whoever asked you the question in the first place.',
  },
  {
    title: 'Automate the extraction you do every time',
    body: 'If you pull the same information out of every new document that arrives, describe it once as an agent and have it done on upload instead.',
  },
];

export default function LandingPage() {
  const login = useSignIn();
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  // Read from the API rather than written here, so the length promised on this
  // page is the length Checkout actually grants. Zero means no trial, which is
  // also what a failed lookup leaves it at - the page then simply says nothing
  // about one rather than advertising a trial that may not exist.
  const [trialDays, setTrialDays] = useState(0);
  const current = useCurrentSection(SECTION_IDS);

  useEffect(() => {
    let live = true;
    fetchBillingConfig().then((config) => {
      if (live) setTrialDays(Number(config?.trialDays) || 0);
    });
    return () => { live = false; };
  }, []);

  async function choose(planId) {
    setError('');
    const trimmedEmail = email.trim();
    const trimmedOrg = orgName.trim();
    // Named by their label, so an error points at the box it means.
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setError('Please fill in "Work email" above — it needs a valid email address.');
      return;
    }
    if (!trimmedOrg) {
      setError('Please fill in "Organisation name" above.');
      return;
    }
    setBusy(planId);
    try {
      const data = await startCheckout({ plan: planId, email: trimmedEmail, orgName: trimmedOrg });
      if (data?.url) {
        // Remembered so the post-checkout page can poll provisioning status.
        try { localStorage.setItem('ydyai_signup_email', trimmedEmail); } catch { /* private mode */ }
        window.location.href = data.url;
        return;
      }
      // Prefer the server's own `message` when it sends one. Without this the
      // refusals that have something useful to say - an email that already
      // owns a workspace, a tier that is not self-serve - showed the bare
      // error code instead, so the one case where the visitor can actually fix
      // it themselves read as "account_exists".
      setError(data?.message
        || (data?.reason === 'stripe_not_configured'
          ? "Checkout isn't configured yet (Stripe keys are not set on the server)."
          : (data?.error || 'Could not start checkout. Please try again.')));
    } catch {
      setError("Couldn't reach the billing service. If you're running locally, that's expected without the backend.");
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="public">
      <PublicHeader sections={SECTIONS} current={current} />

      <section className="hero">
        <div className="hero-inner">
          <HeroWordmark />
          {/* The closed-corpus promise belongs here rather than further down
              the page: it is the thing that separates this from asking a
              chatbot, and it is what a stranger needs in the first sentence.
              Deliberately "from your records and nothing else" rather than
              "never references anything outside" - the model is instructed to
              answer only from the retrieved records (chatWithData.js), which
              is a design, not a law of physics. The citation is the part that
              carries no caveat, because the reader can check it. */}
          <p className="hero-sub">
            Upload the documents you already have and ask a question across all of them.
            Every answer is built from your records and nothing else — and cites the page
            it came from, so you can check it.
          </p>
          <div className="hero-actions">
            <a className="btn" href="#pricing">See pricing</a>
            <button type="button" className="btn secondary" onClick={login.start} disabled={login.busy}>
              {login.busy ? 'Redirecting…' : 'Log in'}
            </button>
          </div>
          <ErrorText>{login.error}</ErrorText>
        </div>
      </section>

      <main className="public-main">
        <section id="about" className="section">
          <div className="section-head">
            <h2>About</h2>
            <p className="sub">
              Every project runs on documents that don’t talk to each other — a specification in one
              system, a supplier’s data sheet in another, a spreadsheet on somebody’s desktop, a
              notebook in somebody’s bag. The answer is usually in there. Finding it means knowing
              which file to open.
            </p>
          </div>
          <div className="about-grid">
            <div className="about-card">
              <div className="about-card-title">Everything you upload becomes searchable</div>
              <p>
                Contracts, drawings, specifications, spreadsheets, photographs, scanned notes. No
                fixed templates and no list of supported document types to work around — whatever
                you put in can be cross-referenced against whatever else is in there.
              </p>
            </div>
            <div className="about-card">
              <div className="about-card-title">Answers come from your records alone</div>
              <p>
                A general chatbot answers from whatever it absorbed in training, and it sounds
                exactly as confident whether or not it knows the first thing about your project.
                Here the only material an answer can be built from is the material you uploaded —
                and every answer names the documents it drew on, so you can open the page and check
                it against the source. An answer you can’t verify isn’t worth having.
              </p>
            </div>
            <div className="about-card">
              <div className="about-card-title">You bring your own AI</div>
              <p>
                Connect your own provider accounts and pick a model for each job rather than one for
                everything — something quick and cheap for reading scans, something stronger for the
                questions that matter. Switching is a dropdown, and it applies to the next run. Your
                documents are processed under your own agreement with that provider, on your own key,
                and you can see what each run cost.
              </p>
            </div>
          </div>
        </section>

        <section id="use-cases" className="section">
          <div className="section-head">
            <h2>Use cases</h2>
            <p className="sub">
              The same thing in every case: a question you’d otherwise answer by opening files one
              at a time.
            </p>
          </div>
          <div className="use-case-grid">
            {USE_CASES.map((u) => (
              <div key={u.title} className="use-case">
                <div className="use-case-title">{u.title}</div>
                <p className="use-case-body">{u.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="pricing" className="section">
          <div className="public-head">
            <h2>Pricing</h2>
            <p className="sub">
              {trialDays
                ? `Free for ${trialDays} days. Cancel before it ends and you won't be charged.`
                : 'Start in minutes. Upgrade, downgrade or cancel anytime.'}
            </p>
            {/* Visible labels rather than placeholders alone. Two identically
                sized boxes with the organisation on top is an easy way to type
                your email into the wrong one and then be told to enter an
                email - which is exactly what the placeholder-only version did. */}
            <div className="public-signup">
              <div className="public-field">
                <label className="field-label" htmlFor="signup-org">Organisation name</label>
                <input
                  id="signup-org"
                  className="input"
                  type="text"
                  autoComplete="organization"
                  placeholder="e.g. Riverside Construction"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                />
              </div>
              <div className="public-field">
                <label className="field-label" htmlFor="signup-email">Work email</label>
                <input
                  id="signup-email"
                  className="input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            {error ? <p className="error" role="alert">{error}</p> : null}
          </div>

          <div className="plan-grid">
            {TIERS.map((t) => (
              <div key={t.id} className={`plan${t.highlight ? ' plan-featured' : ''}`}>
                {t.highlight ? <span className="plan-badge">Most popular</span> : null}
                <div className="plan-name">{t.name}</div>
                <div className="plan-price">
                  <span className="plan-amount">{t.price}</span>
                  {t.cadence ? <span className="plan-cadence">{t.cadence}</span> : null}
                </div>
                {/* Says when the charge starts, not just that something is free.
                    "Free for 7 days" alone reads as though nothing is owed
                    afterwards, and a card IS taken at checkout. */}
                {trialDays && !t.contact ? (
                  <div className="plan-trial">
                    Free for {trialDays} day{trialDays === 1 ? '' : 's'}, then {t.price}{t.cadence || ''}
                  </div>
                ) : null}
                <p className="plan-blurb">{t.blurb}</p>
                <ul className="plan-list">
                  {t.limits.map((l) => <li key={l} className="plan-limit">{l}</li>)}
                  {t.features.map((ft) => <li key={ft} className="plan-feature">{ft}</li>)}
                </ul>
                {t.contact ? (
                  <a className="btn block secondary" href="#contact">Talk to us</a>
                ) : (
                  <button
                    type="button"
                    className={`btn block${t.highlight ? '' : ' secondary'}`}
                    disabled={!!busy}
                    onClick={() => choose(t.id)}
                  >
                    {busy === t.id
                      ? 'Starting…'
                      : trialDays ? `Start ${trialDays}-day free trial` : `Choose ${t.name}`}
                  </button>
                )}
              </div>
            ))}
          </div>

          <p className="public-note">
            Already have an account?{' '}
            <button type="button" className="linklike" onClick={login.start} disabled={login.busy}>
              {login.busy ? 'Redirecting…' : 'Log in with Microsoft Entra'}
            </button>
          </p>
          <ErrorText>{login.error}</ErrorText>
        </section>

        <section id="contact" className="section">
          <div className="section-head">
            <h2>Get in touch</h2>
            <p className="sub">
              Questions, a walkthrough, or a plan that doesn’t fit the three above — write to us and
              a person will reply.
            </p>
          </div>
          <div className="contact-card">
            <div className="contact-row">
              <div>
                <div className="contact-label">Sales and enquiries</div>
                <a className="contact-value" href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>
              </div>
              <a className="btn" href={`mailto:${LEGAL.contactEmail}`}>Email us</a>
            </div>
            <div className="contact-row">
              <div>
                <div className="contact-label">Existing customers</div>
                <a className="contact-value" href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a>
              </div>
              <a className="btn secondary" href={`mailto:${LEGAL.supportEmail}`}>Contact support</a>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
